"use client"

import { Slot } from "radix-ui"
import { createContext, useContext } from "react"
import { type Accept, type FileRejection, useDropzone } from "react-dropzone"
import { useStableCallback } from "../../hooks/useStableCallback"
import { isDev } from "../../lib/constants"

const FileSelectorContext = createContext<() => void>(() => {})

type EventPropName = `on${Uppercase<string>}${string}`
export function FileSelector({
  children,
  accept,
  maxSize = 10 * 1024 * 1024,
  maxFiles = 10,
  multiple = true,
  onFiles,
  onRejections = () => {},
  asTrigger = false,
  handler,
}: {
  children: React.ReactNode
  accept?: Accept
  /**
   * The maximum size of a file in bytes.
   * @default 10 MB (10 * 1024 * 1024)
   */
  maxSize?: number
  /**
   * The maximum number of files that can be selected.
   * @default 10
   */
  maxFiles?: number
  /**
   * Whether to allow multiple files to be selected.
   * @default true
   */
  multiple?: boolean
  onFiles: (files: File[]) => void
  /**
   * Called when the user selects files that do not meet the criteria set by the
   * accept, maxSize, or maxFiles props.
   *
   * **Note:** When the file dialog is triggered (as opposed to dropping files),
   * the user will not be able to select files that do not meet the criteria set
   * by accept, but they can still select too many files or files that are too
   * large.
   */
  onRejections?: (rejections: FileRejection[]) => void
  /**
   * Whether to render the children as a trigger that opens the file dialog when
   * clicked. If `false`, you will need to use {@link FileSelector.Trigger} to
   * open the file dialog.
   *
   * @default false
   */
  asTrigger?: boolean
  /**
   * Only used when `asTrigger` is `true`. The event prop to use to open the
   * file dialog.
   *
   * @default "onClick"
   */
  handler?: EventPropName
}) {
  const onDropAccepted = useStableCallback(onFiles)
  const onDropRejected = useStableCallback(
    onRejections ??
      ((rejections) => {
        if (isDev) {
          for (const rejection of rejections) {
            // eslint-disable-next-line no-console
            console.warn(
              "If you are limiting the types of files that can be uploaded, make sure to handle the rejections by adding an onRejections handler to FileSelector.",
            )
            // eslint-disable-next-line no-console
            console.warn(
              `FileRejection: ${rejection.file.name}\n\n${rejection.errors.map((e) => e.message).join("\n")}`,
            )
          }
        }
      }),
  )

  const dropzone = useDropzone({
    accept,
    maxSize,
    maxFiles,
    multiple,
    onDropAccepted,
    onDropRejected,
  })

  return (
    <FileSelectorContext.Provider value={dropzone.open}>
      <input {...dropzone.getInputProps()} />
      {asTrigger ? <Trigger handler={handler}>{children}</Trigger> : children}
    </FileSelectorContext.Provider>
  )
}

const Trigger = ({
  children,
  handler: using = "onClick",
}: {
  children: React.ReactNode
  /**
   * The event prop to use to open the file dialog.
   *
   * @default "onClick"
   */
  handler?: `on${Uppercase<string>}${string}`
}) => {
  const open = useContext(FileSelectorContext)
  return <Slot.Root {...{ [using]: open }}>{children}</Slot.Root>
}

FileSelector.Trigger = Trigger
